// api.ts:
import type { CantonCode } from '../../shared/cantons';
import axios, { AxiosError, AxiosProgressEvent, InternalAxiosRequestConfig } from 'axios';

// Token storage: access token in memory, refresh token in sessionStorage
// sessionStorage survives page reloads but clears when the tab is closed
let _accessToken: string | null = null;

// Window event dispatched when the session can no longer be renewed (refresh
// token missing, expired or revoked). AuthContext listens for it and shows the
// login screen.
export const SESSION_EXPIRED_EVENT = 'session-expired';

export class SessionExpiredError extends Error {
  constructor(message: string = 'Sessione scaduta') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

// Token management functions
export const getAccessToken = (): string | null => {
  return _accessToken;
};

export const getRefreshToken = (): string | null => {
  try {
    return sessionStorage.getItem('_rt');
  } catch {
    return null;
  }
};

export const setTokens = (accessToken: string, refreshToken: string): void => {
  _accessToken = accessToken;
  try {
    sessionStorage.setItem('_rt', refreshToken);
  } catch { /* ignore */ }
};

export const clearTokens = (): void => {
  _accessToken = null;
  try {
    sessionStorage.removeItem('_rt');
  } catch { /* ignore */ }
};

// Drop the tokens and tell the app the user must log in again.
export const expireSession = (): void => {
  clearTokens();
  try {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  } catch { /* ignore (non-browser env) */ }
};

// Check if token is expired or about to expire
export const isTokenExpiringSoon = (token: string | null, bufferMinutes: number = 5): boolean => {
  if (!token) return true;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiresAt = payload.exp * 1000;
    const now = Date.now();
    const timeUntilExpiry = expiresAt - now;
    return timeUntilExpiry < bufferMinutes * 60 * 1000;
  } catch (error) {
    return true;
  }
};

// Single in-flight refresh shared by every caller (axios interceptor,
// getFreshAccessToken, SSE streams): N parallel 401s trigger ONE
// /auth/refresh call instead of N racing ones, and a late failure can't wipe
// tokens that another refresh has just stored.
let refreshPromise: Promise<string> | null = null;

const performRefresh = async (): Promise<string> => {
  const currentRefresh = getRefreshToken();
  if (!currentRefresh) {
    expireSession();
    throw new SessionExpiredError('No refresh token available');
  }

  let response;
  try {
    response = await axios.post('/api/v1/auth/refresh', {
      refresh_token: currentRefresh
    });
  } catch (error) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    if (status === undefined || status >= 500) {
      // Backend unreachable or restarting: keep the session, the caller can retry.
      throw error;
    }
    // Refresh token rejected (expired/revoked). Only wipe it if nobody stored
    // a newer one in the meantime.
    if (getRefreshToken() === currentRefresh) {
      expireSession();
    }
    throw new SessionExpiredError();
  }

  const { access_token, refresh_token: new_refresh_token } = response.data || {};
  if (!access_token || !new_refresh_token) {
    if (getRefreshToken() === currentRefresh) {
      expireSession();
    }
    throw new SessionExpiredError('Invalid refresh response');
  }

  setTokens(access_token, new_refresh_token);
  return access_token;
};

// Refresh the access token (deduplicated). Rejects with SessionExpiredError
// when the session cannot be renewed (the 'session-expired' event has then
// already been dispatched), or with the network error on transient failures.
export const refreshAccessToken = (): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// Get a fresh token (refresh if needed)
export const getFreshAccessToken = async (): Promise<string | null> => {
  const currentToken = getAccessToken();

  if (currentToken && !isTokenExpiringSoon(currentToken)) {
    return currentToken;
  }

  try {
    return await refreshAccessToken();
  } catch (error) {
    // Transient refresh failure: fall back to the current token if it is
    // still valid for a little while.
    if (currentToken && !isTokenExpiringSoon(currentToken, 0)) {
      return currentToken;
    }
    return null;
  }
};

// Create axios instance
const instance = axios.create({
  baseURL: '',
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
instance.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Endpoints whose 401 must not trigger a token refresh (wrong credentials on
// login, the refresh call itself, logout).
const AUTH_ENDPOINTS_WITHOUT_REFRESH = ['/api/v1/auth/login', '/api/v1/auth/refresh', '/api/v1/auth/logout'];

// Add response interceptor to handle token refresh
instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const url = originalRequest?.url || '';

    // If error is 401 and we haven't already tried to refresh
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !AUTH_ENDPOINTS_WITHOUT_REFRESH.some(endpoint => url.startsWith(endpoint))
    ) {
      originalRequest._retry = true;

      try {
        // If another request already refreshed the token after this one was
        // sent, just retry with the new token instead of refreshing again.
        const sentAuthorization = originalRequest.headers?.['Authorization'];
        const currentToken = getAccessToken();
        const accessToken = currentToken && sentAuthorization !== `Bearer ${currentToken}`
          ? currentToken
          : await refreshAccessToken();

        originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
        return instance(originalRequest);
      } catch (refreshError) {
        // refreshAccessToken already cleared the session if it is expired.
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default instance;

// ======================================
// Shared helpers
// ======================================

const API = '/api/v1';

const MULTIPART = { 'Content-Type': 'multipart/form-data' };

// Uploads also wait for the server-side processing (OCR, embeddings)
const UPLOAD_TIMEOUT_MS = 600000;

type Id = number | string;

const uploadProgress = (onProgress?: (percent: number) => void) =>
  onProgress
    ? (event: AxiosProgressEvent) => {
        if (event.total) onProgress(Math.round((event.loaded * 100) / event.total));
      }
    : undefined;

const postForm = <T = any>(url: string, form: FormData, onProgress?: (percent: number) => void) =>
  instance.post<T>(url, form, {
    headers: MULTIPART,
    timeout: UPLOAD_TIMEOUT_MS,
    onUploadProgress: uploadProgress(onProgress),
  });

/** File name from a Content-Disposition header: the RFC 5987 `filename*`
 * (UTF-8, percent-encoded) when present, else the plain `filename`. */
export const filenameFromDisposition = (header: string | undefined, fallback: string): string => {
  if (!header) return fallback;
  const extended = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      /* malformed encoding: use the plain name below */
    }
  }
  const plain = header.match(/filename\s*=\s*"([^"]*)"/i) || header.match(/filename\s*=\s*([^;]+)/i);
  return plain?.[1]?.trim() || fallback;
};

/** Fetch a file with the Bearer client and hand it to the browser as a
 * download, under the name the server gives (Content-Disposition). */
const saveDownload = async (url: string, fallbackName: string): Promise<void> => {
  const response = await instance.get(url, { responseType: 'blob' });
  const name = filenameFromDisposition(response.headers?.['content-disposition'], fallbackName);
  const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.setAttribute('download', name);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};

// ======================================
// Auth
// ======================================

// Login: stores the token pair (access token in memory, refresh token in
// sessionStorage). The response carries the profile under `user`.
export const login = async (email: string, password: string) => {
  const response = await instance.post('/api/v1/auth/login', { email, password });
  const { access_token, refresh_token } = response.data;
  if (access_token && refresh_token) {
    setTokens(access_token, refresh_token);
  }
  return response;
};

// Refresh access token (kept for backward compatibility; shares the single
// in-flight refresh with the interceptor).
export const refreshToken = async () => {
  const access_token = await refreshAccessToken();
  return { data: { access_token, refresh_token: getRefreshToken() } };
};

export const logout = async () => {
  try {
    const response = await instance.post('/api/v1/auth/logout');
    clearTokens();
    return response;
  } catch (error) {
    clearTokens();
    throw error;
  }
};

// Has an access token, or a refresh token to obtain one
export const isAuthenticated = (): boolean => {
  return !!getAccessToken() || !!getRefreshToken();
};

// The profile: email, full_name, role, firm, capability flags, preferred_canton
export const getCurrentUser = async () => {
  return instance.get('/api/v1/auth/me');
};

// Change the current user's password. The server revokes every session of the
// user and returns a fresh token pair bound to a new session: store it so this
// tab stays logged in.
export const changePassword = async (currentPassword: string, newPassword: string) => {
  const response = await instance.post('/api/v1/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword
  });
  const { access_token, refresh_token } = response.data || {};
  if (access_token && refresh_token) {
    setTokens(access_token, refresh_token);
  }
  return response;
};

// Update current user preferences (e.g. preferred_canton)
export const updateUserPreferences = (preferences: { preferred_canton?: string }) => {
  return instance.patch('/api/v1/auth/me/preferences', preferences);
};

// ======================================
// Chat
// ======================================

// Streaming itself lives in features/chat/api/chatStream.ts (fetch + SSE).
export const clearChatHistory = async () => {
  return instance.post(`${API}/chat-stream/clear-history`);
};

export interface TranslationResult { translation: string; disclaimer: string }

// Unofficial LLM translation of a source passage (only the original is authoritative)
export const translateText = (text: string, target_language: string) =>
  instance.post<TranslationResult>(`${API}/translate`, { text, target_language });

// ======================================
// Legal acts (shared corpus: reads for everyone, writes for the super admin)
// ======================================

export type Jurisdiction = 'CH' | 'INT' | CantonCode;
export type LegalStatusValue = 'in_vigore' | 'abrogato' | 'sconosciuto';

export interface LegalStatusFields {
  legal_status: LegalStatusValue;
  legal_status_source: string | null;
  legal_status_reference: string | null;
  legal_status_url: string | null;
  legal_status_checked_at: string | null;
  text_version_date: string | null;
}

export interface LegalAct extends LegalStatusFields {
  id: number;
  jurisdiction: Jurisdiction;
  number: string | null;
  language: string | null;
  languages?: string[];
  title: string;
  short_title: string | null;
  act_type: string | null;
  source: string | null;
  external_id: string | null;
  reference: string | null;
  official_source: string | null;
  official_url: string | null;
  articles_count: number;
}

export interface LegalArticle {
  id: number;
  label: string | null;
  rubric: string | null;
  section_title: string | null;
  content: string;
  sort_order: number | null;
}

export interface LegalActListParams {
  jurisdiction?: Jurisdiction;
  status?: LegalStatusValue;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface LegalActSearchParams {
  q: string;
  jurisdiction?: Jurisdiction;
  mode?: 'text' | 'semantic';
  limit?: number;
}

export interface LegalStatusUpdate {
  legal_status: LegalStatusValue;
  legal_status_reference?: string | null;
  legal_status_url?: string | null;
}

export interface LegalArticleUpdate {
  label?: string;
  rubric?: string;
  section_title?: string;
  content?: string;
  sort_order?: number;
}

export const fetchLegalActs = (params: LegalActListParams = {}) =>
  instance.get<{ items: LegalAct[]; total: number }>(`${API}/legal-acts`, { params });

export const fetchLegalAct = (id: Id) =>
  instance.get<LegalAct>(`${API}/legal-acts/${id}`);

export const fetchLegalActArticles = (id: Id) =>
  instance.get<LegalArticle[]>(`${API}/legal-acts/${id}/articles`);

// The other language versions of an act (id, language) — empty/single when there is only one
export const fetchLegalActVersions = (id: Id) =>
  instance.get<Array<{ id: number; language: string }>>(`${API}/legal-acts/${id}/versions`);

/** The 26 cantons with their acts in force, whether the chat may search them, and the
 * caller's firm default (subproject 3e). */
export interface CantonInfo { code: string; acts: number; available: boolean }
export interface Jurisdictions { cantons: CantonInfo[]; firm_default: string[] }

export const fetchJurisdictions = () =>
  instance.get<Jurisdictions>(`${API}/legal-acts/jurisdictions`);

// mode "text": articles containing q, with a snippet; "semantic": vector hits
export const searchLegalActs = (params: LegalActSearchParams) =>
  instance.get<Array<Record<string, any>>>(`${API}/legal-acts/search`, { params });

export const updateLegalActStatus = (id: Id, body: LegalStatusUpdate) =>
  instance.put<LegalAct>(`${API}/legal-acts/${id}/legal-status`, body);

export const updateLegalArticle = (actId: Id, articleId: Id, body: LegalArticleUpdate) =>
  instance.put<LegalArticle>(`${API}/legal-acts/${actId}/articles/${articleId}`, body);

export const deleteLegalArticle = (actId: Id, articleId: Id) =>
  instance.delete(`${API}/legal-acts/${actId}/articles/${articleId}`);

export const deleteLegalAct = (id: Id) =>
  instance.delete(`${API}/legal-acts/${id}`);

// The official source file (Fedlex XML) of an act
export const downloadLegalActFile = (id: Id) =>
  saveDownload(`${API}/legal-acts/${id}/file`, `atto_${id}.xml`);

// ======================================
// Case law (digests of the shared corpus)
// ======================================

export interface CaseLawDigest {
  id: number;
  title: string | null;
  description: string | null;
  case_number: string | null;
  court_name: string | null;
  decision_date: string | null;
  doc_type: string | null;
  file_name: string | null;
  has_file: boolean;
  created_at: string | null;
  updated_at: string | null;
  content?: string;
}

export interface CaseLawListParams {
  q?: string;
  court?: string;
  limit?: number;
  offset?: number;
}

export interface CaseLawUpdate {
  title?: string;
  description?: string;
  case_number?: string;
  court_name?: string;
  decision_date?: string;
}

export const fetchCaseLaw = (params: CaseLawListParams = {}) =>
  instance.get<{ items: CaseLawDigest[]; total: number }>(`${API}/case-law`, { params });

// Semantic search: payload + score + id (the digest id)
export const searchCaseLaw = (q: string, limit?: number) =>
  instance.get<Array<Record<string, any>>>(`${API}/case-law/search`, { params: { q, limit } });

// Federal court decisions (server /federal-case-law, subproject 3c)
export interface FederalDecisionHit {
  decision_id: string
  court: string
  court_name: string
  reference: string
  docket: string
  decision_date: string
  language: string
  title: string
  citation: string
  snippet: string
  regesto_it: string
  score: number | null
}

export interface FederalSearchParams {
  q: string
  court?: string
  language?: string
  mode?: 'reference' | 'semantic'
  limit?: number
}

export const searchFederalCaseLaw = (params: FederalSearchParams) =>
  instance.get<FederalDecisionHit[]>(`${API}/federal-case-law/search`, { params });

export const fetchFederalDecisionText = (decisionId: string) =>
  instance.get<string>(`${API}/federal-case-law/${encodeURIComponent(decisionId)}/text`, { responseType: 'text' });

// Form fields: file, and optionally title, case_number, court_name, decision_date
export const uploadCaseLaw = (form: FormData, onProgress?: (percent: number) => void) =>
  postForm<CaseLawDigest>(`${API}/case-law/upload`, form, onProgress);

export const updateCaseLaw = (id: Id, body: CaseLawUpdate) =>
  instance.put<CaseLawDigest>(`${API}/case-law/${id}`, body);

export const deleteCaseLaw = (id: Id) =>
  instance.delete(`${API}/case-law/${id}`);

// The original PDF, or the digest text (.txt) when there is none
export const downloadCaseLaw = (id: Id) =>
  saveDownload(`${API}/case-law/${id}/download`, `sentenza_${id}.pdf`);

// ======================================
// Doctrine (shared corpus)
// ======================================

export interface DoctrineItem {
  id: number;
  document_id: number | null;
  edition: string | null;
  sequence_number: number | null;
  year: number | null;
  volume: string | null;
  title: string;
  authors: string | null;
  abstract: string | null;
  source_url: string | null;
  publication_date: string | null;
  language: string | null;
  section_count: number | null;
  subsection_count: number | null;
  has_toc: boolean | null;
  has_footnotes: boolean | null;
  footnote_count: number | null;
  primary_laws_cited: string[];
  articles_cited: string[];
  case_references: string[];
  jurisdictions: string[];
  subject_areas: string[];
  keywords: string[];
  embedding_status: string | null;
  embedding_model: string | null;
  chunk_count: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface DoctrineContent {
  content: string;
  title: string;
  authors: string | null;
  edition: string | null;
  document_id: number | null;
}

export interface DoctrineUpdate {
  title?: string;
  authors?: string;
  abstract?: string;
  subject_areas?: string[];
  keywords?: string[];
}

export const fetchDoctrine = (params: { q?: string; limit?: number; offset?: number } = {}) =>
  instance.get<{ items: DoctrineItem[]; total: number }>(`${API}/doctrine`, { params });

export const fetchDoctrineItem = (id: Id) =>
  instance.get<DoctrineItem>(`${API}/doctrine/${id}`);

export const fetchDoctrineContent = (id: Id) =>
  instance.get<DoctrineContent>(`${API}/doctrine/${id}/content`);

// Semantic search: the best chunk of each of the closest articles
export const searchDoctrine = (q: string, limit?: number) =>
  instance.post<Array<Record<string, any>>>(`${API}/doctrine/search`, { query: q, limit });

// Form field: file (markdown, "<sequence>_<document id>.md")
export const uploadDoctrine = (form: FormData, onProgress?: (percent: number) => void) =>
  postForm<DoctrineItem>(`${API}/doctrine/upload`, form, onProgress);

export const updateDoctrine = (id: Id, body: DoctrineUpdate) =>
  instance.put<DoctrineItem>(`${API}/doctrine/${id}`, body);

export const deleteDoctrine = (id: Id) =>
  instance.delete(`${API}/doctrine/${id}`);

// The markdown text of the article
export const downloadDoctrine = (id: Id) =>
  saveDownload(`${API}/doctrine/${id}/download`, `dottrina_${id}.md`);

// ======================================
// Firm documents
// ======================================

export interface FirmDocument {
  id: number;
  firm_id: string;
  title: string | null;
  description: string | null;
  document_type_code: string | null;
  document_type_name: string | null;
  file_name: string | null;
  file_available: boolean;
  vector_status: string | null;
  uploaded_by_user_id: string | null;
  uploaded_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  can_edit?: boolean;
  can_delete?: boolean;
  can_reprocess?: boolean;
  content?: string | null;
  tables?: unknown;
}

export interface DocumentUpdate {
  title?: string;
  description?: string;
  document_type_code?: string;
}

export interface DocumentTypeCatalog {
  categories: Array<{ slug: string; label: string; types: Array<{ code: string; name: string }> }>;
}

// firm_id: super admin only (a member always reads their own firm)
export const fetchDocuments = (params: { firm_id?: string; skip?: number; limit?: number } = {}) =>
  instance.get<{ documents: FirmDocument[]; total: number; skip: number; limit: number }>(
    `${API}/documents`, { params });

export const fetchDocument = (id: Id) =>
  instance.get<FirmDocument>(`${API}/documents/${id}`);

// Form fields: file, and optionally document_type_code, extraction_mode
// ("standard" | "vision"), firm_id (super admin). The server answers at once
// with a scan_id and processes the file in the background (scan events).
export const uploadDocument = (form: FormData, onProgress?: (percent: number) => void) =>
  postForm<{ status: string; scan_id: string }>(`${API}/documents/upload`, form, onProgress);

export const updateDocument = (id: Id, body: DocumentUpdate) =>
  instance.put<FirmDocument>(`${API}/documents/${id}`, body);

export const deleteDocument = (id: Id) =>
  instance.delete(`${API}/documents/${id}`);

// Runs the pipeline again on the original file (new id), in the background
export const reprocessDocument = (id: Id, extractionMode: 'standard' | 'vision' = 'standard') =>
  instance.post(`${API}/documents/${id}/reprocess`, null, { params: { extraction_mode: extractionMode } });

// Hybrid search (vectors + keywords) in the firm's documents
export const searchDocuments = (q: string, limit?: number) =>
  instance.get<{ documents: Array<Record<string, any>>; total: number; query: string }>(
    `${API}/documents/search`, { params: { q, limit } });

// Fetch the PDF with the Bearer client and return a blob: URL for the viewer
// (never the API URL: the server takes no cookies). The caller revokes it
// with URL.revokeObjectURL when the viewer closes.
export const viewDocumentPdf = async (id: Id): Promise<string> => {
  const response = await instance.get(`${API}/documents/${id}/view`, { responseType: 'blob' });
  return window.URL.createObjectURL(response.data);
};

export const downloadDocumentFile = (id: Id) =>
  saveDownload(`${API}/documents/${id}/file`, `documento_${id}.pdf`);

// Cancel an upload or reprocess in progress
export const abortScan = (scanId: string) =>
  instance.post(`${API}/documents/scans/${encodeURIComponent(scanId)}/abort`);

// The global catalogue of document types, grouped by category
export const fetchDocumentTypes = () =>
  instance.get<DocumentTypeCatalog>(`${API}/document-types`);

// ======================================
// Bank statements (firm)
// ======================================

export interface BankStatement {
  id: string;
  firm_id: string;
  title: string | null;
  description: string | null;
  bank_name: string | null;
  account_number: string | null;
  period_start: string | null;
  period_end: string | null;
  file_name: string | null;
  uploaded_by_user_id: string | null;
  created_at: string | null;
  transactions_count: number;
  transactions?: Array<Record<string, any>>;
  can_delete?: boolean;
}

// A bare list (not wrapped), newest first
export const fetchBankStatements = (params: { firm_id?: string } = {}) =>
  instance.get<BankStatement[]>(`${API}/bank-statements`, { params });

// One statement with all its transactions
export const getBankStatement = (id: Id) =>
  instance.get<BankStatement>(`${API}/bank-statements/${id}`);

// Form fields: file, and optionally firm_id (super admin)
export const uploadBankStatement = (form: FormData, onProgress?: (percent: number) => void) =>
  postForm(`${API}/bank-statements/upload`, form, onProgress);

export const deleteBankStatement = (id: Id) =>
  instance.delete(`${API}/bank-statements/${id}`);

export const downloadBankStatement = (id: Id) =>
  saveDownload(`${API}/bank-statements/${id}/download`, `estratto_conto_${id}`);

// Wrapped: {statements: [...]}
export const searchBankStatements = (q: string, type: 'simple' | 'semantic' = 'simple') =>
  instance.get<{ statements: BankStatement[] }>(`${API}/bank-statements/search`, { params: { q, type } });

// ======================================
// Internal policies of the firm
// ======================================

export interface Policy {
  id: number;
  firm_id: string;
  title: string;
  description: string | null;
  category: string | null;
  content: string;
  priority: number;
  is_active: boolean;
  created_by_user_id: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PolicyCreate {
  title: string;
  content: string;
  description?: string;
  category?: string;
  priority?: number;
  firm_id?: string;
}

export type PolicyUpdate = Partial<Omit<PolicyCreate, 'firm_id'>> & { is_active?: boolean };

// includeInactive: only honoured for whoever manages the firm. firm_id: the
// super admin only, to list one firm's policies (a member's own firm is
// implicit and the param is ignored for them).
export const fetchPolicies = (includeInactive: boolean = false, params: { firm_id?: string } = {}) =>
  instance.get<Policy[]>(`${API}/policies`, { params: { include_inactive: includeInactive, firm_id: params.firm_id } });

export const createPolicy = (body: PolicyCreate) =>
  instance.post<Policy>(`${API}/policies`, body);

export const updatePolicy = (id: Id, body: PolicyUpdate) =>
  instance.put<Policy>(`${API}/policies/${id}`, body);

export const deletePolicy = (id: Id) =>
  instance.delete(`${API}/policies/${id}`);

// ======================================
// Firm members
// ======================================

export interface Member {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  firm_id: string | null;
  firm_name: string | null;
  is_active: boolean;
  created_at: string;
}

export interface MemberCreate {
  email: string;
  role: string;
  full_name?: string;
  firm_id?: string;
  password?: string;
}

export interface MemberUpdate {
  email?: string;
  full_name?: string;
  role?: string;
  is_active?: boolean;
  password?: string;
}

export const fetchMembers = (params: { firm_id?: string } = {}) =>
  instance.get<{ members: Member[]; total: number }>(`${API}/members`, { params });

// Without a password the server generates one: `generated_password`, once
export const createMember = (body: MemberCreate) =>
  instance.post<Member & { generated_password?: string }>(`${API}/members`, body);

export const updateMember = (id: string, body: MemberUpdate) =>
  instance.put<Member>(`${API}/members/${id}`, body);

// A member who owns data is deactivated instead ({deactivated: true})
export const deleteMember = (id: string) =>
  instance.delete<{ deactivated: boolean; message: string }>(`${API}/members/${id}`);

export const resetMemberPassword = (id: string) =>
  instance.post<{ message: string; new_password: string }>(`${API}/members/${id}/reset-password`);

// ======================================
// Firms
// ======================================

export interface FirmSummary {
  id: string;
  name: string;
  code: string;
  member_count: number;
  created_at: string;
}

export interface Letterhead {
  company_name?: string;
  address?: string;
  postal_code_city?: string;
  phone?: string;
  email?: string;
  website?: string;
  footer?: string;
  logo_path?: string;
}

export interface MyFirm {
  id: string;
  name: string;
  code: string;
  letterhead: Letterhead;
  cantons?: string[];
}

// Super admin
export const fetchFirms = () =>
  instance.get<{ firms: FirmSummary[]; total: number }>(`${API}/firms`);

export const createFirm = (body: { name: string; code: string }) =>
  instance.post<FirmSummary>(`${API}/firms`, body);

export const updateFirm = (id: string, body: { name?: string; code?: string }) =>
  instance.put<FirmSummary>(`${API}/firms/${id}`, body);

// 409 while the firm still has members or data
export const deleteFirm = (id: string) =>
  instance.delete(`${API}/firms/${id}`);

// The caller's own firm (400 for the super admin, who has none)
export const fetchMyFirm = () =>
  instance.get<MyFirm>(`${API}/firms/mine`);

export const updateLetterhead = (body: Omit<Letterhead, 'logo_path'>) =>
  instance.put<MyFirm>(`${API}/firms/mine/letterhead`, body);

export const updateFirmCantons = (cantons: string[]) =>
  instance.put<MyFirm>(`${API}/firms/mine/cantons`, { cantons });

// PNG or JPEG, at most 1 MB; the server checks the content (422 on an unreadable image)
export const uploadFirmLogo = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return postForm<MyFirm>(`${API}/firms/mine/logo`, form);
};

// Super admin: any firm with its letterhead (the same shape as /firms/mine)
export const fetchFirm = (id: string) =>
  instance.get<MyFirm>(`${API}/firms/${id}`);

export const updateFirmLetterhead = (id: string, body: Omit<Letterhead, 'logo_path'>) =>
  instance.put<MyFirm>(`${API}/firms/${id}/letterhead`, body);

export const updateFirmCantonsFor = (firmId: string, cantons: string[]) =>
  instance.put<MyFirm>(`${API}/firms/${firmId}/cantons`, { cantons });

export const uploadLogoForFirm = (id: string, file: File) => {
  const form = new FormData();
  form.append('file', file);
  return postForm<MyFirm>(`${API}/firms/${id}/logo`, form);
};

// ======================================
// Summaries
// ======================================

export interface GeneratedSummary {
  summary: string;
  created_at: string;
  section: string;
  metadata?: Record<string, any> | null;
}

export interface SavedSummary {
  id: string;
  section: string;
  summary_content: string;
  created_at: string;
  generation_metadata?: Record<string, any> | null;
}

// Generate (and save) a summary: "documents" (the firm's documents; the super
// admin names the firm with {firm_id}) or "legal-acts" (params: {jurisdiction: "CH" | "TI"})
export const fetchSummary = (section: 'documents' | 'legal-acts',
                             params: { jurisdiction?: Jurisdiction; firm_id?: string } = {}) =>
  instance.get<GeneratedSummary>(`${API}/summaries/${section}`, { params });

// The caller's latest saved summary of a section ("documents", "legal_acts";
// for "legal_acts" pass {jurisdiction} to get that one's)
export const getLatestSummary = (section: string, params: { jurisdiction?: Jurisdiction } = {}) =>
  instance.get<SavedSummary | null>(`${API}/summaries/${section}/latest`, { params });

// ======================================
// Prompts Management API
// ======================================

export const fetchPrompts = async () => {
  return instance.get("/api/v1/prompts/");
};

export const fetchPromptsByCategory = async (category: string) => {
  return instance.get(`/api/v1/prompts/category/${category}`);
};

export const getPromptByKey = async (promptKey: string) => {
  return instance.get(`/api/v1/prompts/${promptKey}`);
};

export const updatePrompt = async (promptKey: string, data: { content: string; name?: string; description?: string }) => {
  return instance.put(`/api/v1/prompts/${promptKey}`, data);
};

export const resetPrompt = async (promptKey: string) => {
  return instance.post(`/api/v1/prompts/${promptKey}/reset`);
};

export const createPrompt = async (data: {
  key: string;
  name: string;
  category: string;
  content: string;
  description?: string
}) => {
  return instance.post("/api/v1/prompts/", data);
};

export const clearPromptsCache = async () => {
  return instance.post("/api/v1/prompts/cache/clear");
};

// ======================================
// Database Maintenance API
// ======================================

export const checkDatabaseConsistency = async () => {
  return instance.get("/api/v1/maintenance/check");
};

export const cleanupDatabase = async (data: { confirm: boolean }) => {
  return instance.post("/api/v1/maintenance/cleanup", data);
};

export const getDatabaseStats = async () => {
  return instance.get("/api/v1/maintenance/stats");
};

// Continuous update of the legal sources (nightly job + manual run)
export const getSourceUpdates = async () => {
  return instance.get("/api/v1/maintenance/source-updates");
};

export const runSourceUpdates = async () => {
  return instance.post("/api/v1/maintenance/source-updates/run");
};
